import { ZoomPhoneCallState } from '@ndvr/customer-api';

import { formatPhone } from 'views/household/HouseholdTexts/text-utils';

import { MessageRow } from './message-types';

export type MessageTypeFilter = 'all' | 'email' | 'text' | 'call';

export interface MessageFilters {
    type: MessageTypeFilter;
    callStates: ZoomPhoneCallState[];
    searchTerm: string;
}

/**
 * How to read one side of a row for display: the party's name, and the address or number shown as its
 * subtext. Kept here beside {@link searchableFields} so the per-type field knowledge lives in one place.
 * Note the two differ on purpose — search matches raw digits, display shows a formatted number.
 */
export const party = (row: MessageRow, side: 'from' | 'to'): { name: string; detail: string } => {
    if (row.type === 'call') {
        const { fromName, fromNumber, toName, toNumber } = row.call;
        return side === 'from'
            ? { name: fromName ?? '', detail: formatPhone(fromNumber) }
            : { name: toName ?? '', detail: formatPhone(toNumber) };
    }
    if (row.type === 'email') {
        return side === 'from'
            ? { name: row.fromName, detail: row.fromEmail }
            : { name: row.toName, detail: row.toEmail };
    }
    return side === 'from'
        ? { name: row.fromName, detail: formatPhone(row.fromPhone) }
        : { name: row.toName, detail: formatPhone(row.toPhone) };
};

const searchableFields = (row: MessageRow): (string | undefined)[] => {
    if (row.type === 'email') {
        return [row.subject, row.fromName, row.fromEmail, row.toName, row.toEmail];
    }
    if (row.type === 'call') {
        return [row.call.fromName, row.call.fromNumber, row.call.toName, row.call.toNumber, row.call.noteSummary];
    }
    return [row.preview, row.fromName, row.fromPhone, row.toName, row.toPhone];
};

/** Call statuses describe calls only, so selecting any excludes emails and texts. */
const matchesCallState = (row: MessageRow, callStates: ZoomPhoneCallState[]): boolean =>
    callStates.length === 0 || (row.type === 'call' && callStates.includes(row.call.state));

export const filterMessages = (
    messages: MessageRow[],
    { type, callStates, searchTerm }: MessageFilters,
): MessageRow[] => {
    const term = searchTerm.trim().toLowerCase();
    return messages.filter(
        (row) =>
            (type === 'all' || row.type === type) &&
            matchesCallState(row, callStates) &&
            (!term || searchableFields(row).some((field) => field?.toLowerCase().includes(term))),
    );
};
